import { useEffect, useCallback, useRef, useMemo } from 'react';
import { audioManager } from '../utils/audioManager';
import { Message } from '../types';
import { useChatContext } from '../context/ChatContext';

/**
 * Custom hook for managing automatic audio playback from SSE messages
 * Handles queuing and playback of voice messages with proper cleanup and memory optimization
 */
export const useAudioPlayback = () => {
  const { state, dispatch } = useChatContext();
  const processedMessagesRef = useRef<Set<string>>(new Set());
  const maxProcessedMessages = 100; // Limit to prevent memory leaks

  /**
   * Process new messages for audio playback
   */
  const processMessageForAudio = useCallback((message: Message) => {
    // Skip if message already processed or has no voice data
    if (processedMessagesRef.current.has(message.id) || !message.voice) {
      return;
    }

    // Mark message as processed
    processedMessagesRef.current.add(message.id);

    // Queue audio for playback
    audioManager.queueAudio(
      message.id,
      message.voice,
      {
        onStart: () => {
          console.log(`Started playing audio for message: ${message.id}`);
        },
        onEnd: () => {
          console.log(`Finished playing audio for message: ${message.id}`);
        },
        onError: (error) => {
          console.error(`Audio playback error for message ${message.id}:`, error);
        }
      }
    );

    // Update audio queue in state for UI feedback
    dispatch({ type: 'QUEUE_AUDIO', payload: message.voice });
  }, [dispatch]);

  /**
   * Clean up processed messages set to prevent memory leaks
   */
  const cleanupProcessedMessages = useCallback(() => {
    if (processedMessagesRef.current.size > maxProcessedMessages) {
      const currentMessages = new Set(state.messages.map(m => m.id));
      const newProcessedSet = new Set<string>();
      
      // Keep only processed messages that still exist in current messages
      processedMessagesRef.current.forEach(id => {
        if (currentMessages.has(id)) {
          newProcessedSet.add(id);
        }
      });
      
      processedMessagesRef.current = newProcessedSet;
    }
  }, [state.messages]);

  /**
   * Process all messages for new voice content with memory cleanup
   */
  useEffect(() => {
    // Clean up processed messages first
    cleanupProcessedMessages();
    
    // Process messages for audio
    state.messages.forEach(message => {
      processMessageForAudio(message);
    });
  }, [state.messages, processMessageForAudio, cleanupProcessedMessages]);

  /**
   * Memoized playback control functions to prevent unnecessary re-renders
   */
  const playbackControls = useMemo(() => ({
    play: () => {
      audioManager.play();
    },

    pause: () => {
      audioManager.pause();
    },

    stop: () => {
      audioManager.stop();
      // Clear processed messages when stopping
      processedMessagesRef.current.clear();
    },

    getPlaybackState: () => {
      return audioManager.getPlaybackState();
    },

    setVolume: (volume: number) => {
      audioManager.setDefaultVolume(volume);
    },

    clearQueue: () => {
      audioManager.clearQueue();
    }
  }), []);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      audioManager.cleanup();
    };
  }, []);

  return {
    playbackControls,
    audioQueue: state.audioQueue,
    playbackState: playbackControls.getPlaybackState()
  };
};

/**
 * Hook for components that need to display audio playback status
 */
export const useAudioPlaybackStatus = () => {
  const { state } = useChatContext();
  
  return {
    audioQueue: state.audioQueue,
    queueLength: state.audioQueue.length,
    playbackState: audioManager.getPlaybackState()
  };
};